import { ReactElement, useEffect, useState } from "react";
import { ArrowRight, X, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Combobox,
  ComboboxContent,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
} from "@/shared/components/ui/combobox";
import { Batch } from "@/services/backend/batches/batch/batch";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { GrowthMedium, SampleType } from "@/services/backend/types";
import { growthMedia, sampleTypes } from "@/shared/components/enum-labels";
import {
  ListRequest,
  useListBatchesQuery,
} from "@/services/backend/batches/batch/service";
import {
  ListRequest as ListSampleSetsRequest,
  useUpdateSamplesMutation,
  useSampleSetListQuery,
  UpdateSamplesRequest,
  UpdateSampleRequest,
} from "@/services/backend/sample-set/service";
import {
  Sample,
  SampleBatch,
  SampleSet,
} from "@/services/backend/sample-set/sample-set";
import { cn } from "@/shared/lib/utils";
import { formatDate, getBatchExpiryStatus } from "@/shared/utilities/date";
import { v4 } from "uuid";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import { SampleSetNumber } from "../sample-set/sample-set-number";

interface SampleViewModel {
  sampleSetId: string;
  sample: Sample;
  sampleType: SampleType;
  matchesFilters: boolean;
}

interface BatchAssignmentDialogProps {
  ownerId: string;
  ownerType: string;
  disabled?: boolean;
}

function getSampleTypeBadge(
  sets: SampleSet[] | null | undefined,
  sampleSetId: string,
): ReactElement {
  const foundSet = (sets ?? []).find((set) => set.id === sampleSetId);
  return (
    <Badge variant="outline" className="text-xs">
      {foundSet ? sampleTypes[foundSet.sampleType] : "Kein Typ vorhanden"}
    </Badge>
  );
}

export function BatchAssignmentDialog({
  ownerId,
  ownerType,
  disabled,
}: BatchAssignmentDialogProps) {
  const [updateSample, { error }] = useUpdateSamplesMutation();
  const [selectedBatch, setSelectedBatch] = useState<string>("");
  const [selectedTargets, setSelectedTargets] = useState<string[]>([]);
  const [availableItems, setAvailableItems] = useState<SampleViewModel[]>([]);
  const [allAvailableItems, setAllAvailableItems] = useState<SampleViewModel[]>(
    [],
  );
  const [assignedItems, setAssignedItems] = useState<SampleViewModel[]>([]);
  const [growthMedium, setGrowthMedium] = useState<string>("all");
  const [sampleType, setSampleType] = useState<string>("all");
  // Tracks in-progress local batch assignments across selectedBatch changes.
  // Key: sample.id — Value: desired batch (undefined = explicitly unassigned).
  const [pendingBatch, setPendingBatch] = useState<
    Map<string, SampleBatch | undefined>
  >(new Map());
  const [request, setRequest] = useState<ListRequest>({
    growthMedium: {
      active: growthMedium !== "all",
      values: growthMedium !== "all" ? [growthMedium as GrowthMedium] : [],
    },
    sampleType: {
      active: sampleType !== "all",
      values: sampleType !== "all" ? [sampleType as SampleType] : [],
    },
    limit: 0,
    offset: 0,
  });

  // sampleType filtered client-side (see main derived-state effect) so that
  // changing the filter doesn't drop selections hidden by it.
  const sampleSetRequest: ListSampleSetsRequest = {
    sampleType: { active: false, values: [] },
    ownerId: { active: true, values: [ownerId] },
    ownerType: { active: true, values: [ownerType] },
    limit: 0,
    offset: 0,
  };

  const [batchInputValue, setBatchInputValue] = useState("");

  const { data: batches } = useListBatchesQuery(request);
  const { data: sets } = useSampleSetListQuery(sampleSetRequest);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const activeBatches = (batches?.items ?? []).filter(
    (b) => new Date(b.expiryDate) >= today,
  );
  const expiredBatches = (batches?.items ?? []).filter(
    (b) => new Date(b.expiryDate) < today,
  );
  const filterBatches = (items: Batch[]) =>
    batchInputValue.length === 0
      ? items
      : items.filter((b) =>
          b.batchNumber.toLowerCase().includes(batchInputValue.toLowerCase()),
        );
  const filteredActive = filterBatches(activeBatches);
  const filteredExpired = filterBatches(expiredBatches);
  const showBatches =
    batchInputValue.length > 0 ||
    growthMedium !== "all" ||
    sampleType !== "all";

  useEffect(() => {
    const checkIfSampleIsValid = (
      currentGrowthMedium: GrowthMedium,
      currentSampleType: SampleType,
    ): boolean => {
      if (!batches) {
        return false;
      }
      const batch = (batches.items ?? []).find((b) => b.id === selectedBatch);
      if (!batch) {
        return false;
      }

      return (
        batch.growthMedium === currentGrowthMedium &&
        batch.sampleType === currentSampleType
      );
    };

    const all: SampleViewModel[] = (sets?.items ?? []).flatMap((set) =>
      (set.samples ?? []).map((s) => {
        // Apply pending override; fall back to server data.
        const effectiveSample: Sample = pendingBatch.has(s.id)
          ? { ...s, batch: pendingBatch.get(s.id) }
          : s;
        return {
          sampleSetId: set.id,
          sample: effectiveSample,
          sampleType: set.sampleType,
          matchesFilters: checkIfSampleIsValid(s.growthMedium, set.sampleType),
        };
      }),
    );

    const isAssigned = (vm: SampleViewModel) => {
      const b = vm.sample?.batch;
      if (!b || typeof b !== "object") return false;
      const { id } = b;
      const num = b.batchNumber;
      const hasId =
        (typeof id === "string" && id.trim().length > 0) ||
        (typeof id === "number" && Number.isFinite(id));
      const hasBatchNumber =
        typeof num === "string" ? num.trim().length > 0 : false;

      return hasId || hasBatchNumber;
    };

    const filtered = all.filter(
      (vm) =>
        (growthMedium === "all" || vm.sample.growthMedium === growthMedium) &&
        (sampleType === "all" || vm.sampleType === sampleType),
    );
    const assigned = filtered.filter(isAssigned);
    const available = filtered.filter((vm) => !isAssigned(vm));
    // Unfiltered by growthMedium/sampleType display filters — used to decide
    // what stays selected and what "Proben zuweisen" can act on, so hiding an
    // item behind a filter doesn't silently drop its selection.
    const allAvailable = all.filter((vm) => !isAssigned(vm));

    setAssignedItems(assigned);
    setAvailableItems(available);
    setAllAvailableItems(allAvailable);

    // keep selection only for items that got actually assigned elsewhere,
    // not ones merely hidden by the current display filters
    setSelectedTargets((prev) =>
      prev.filter((id) => allAvailable.some((vm) => vm.sample.id === id)),
    );
  }, [sets, growthMedium, batches, sampleType, selectedBatch, pendingBatch]);

  // Prune a pending override once the refetched server data already reflects it.
  // Clearing pendingBatch eagerly on save would bounce items between panels while
  // the refetch is in flight; dropping entries only when the server matches keeps
  // the merged view stable.
  useEffect(() => {
    if (!sets?.items) return;
    setPendingBatch((prev) => {
      if (prev.size === 0) return prev;
      const next = new Map(prev);
      let changed = false;
      sets.items.forEach((set) =>
        (set.samples ?? []).forEach((s) => {
          if (!next.has(s.id)) return;
          const desiredId = next.get(s.id)?.originalId ?? null;
          const serverId = s.batch?.originalId ?? null;
          if (desiredId === serverId) {
            next.delete(s.id);
            changed = true;
          }
        }),
      );
      return changed ? next : prev;
    });
  }, [sets]);

  useEffect(() => {
    setRequest((r) => ({
      ...r,
      growthMedium: {
        active: growthMedium !== "all",
        values: growthMedium !== "all" ? [growthMedium as GrowthMedium] : [],
      },
      sampleType: {
        active: sampleType !== "all",
        values: sampleType !== "all" ? [sampleType as SampleType] : [],
      },
    }));
  }, [growthMedium, sampleType]);

  const handleAssignment = () => {
    if (!sets?.items || !batches?.items) return;

    const batch = (batches.items ?? []).find((b) => b.id === selectedBatch);
    if (!batch || selectedTargets.length === 0) return;

    const toAssign = allAvailableItems
      .filter((svm) => selectedTargets.includes(svm.sample.id))
      .filter(
        (svm) =>
          svm.sampleType === batch.sampleType &&
          svm.sample.growthMedium === batch.growthMedium,
      );

    if (toAssign.length === 0) {
      setSelectedTargets((prev) =>
        prev.filter((id) => !selectedTargets.includes(id)),
      );
      return;
    }

    setPendingBatch((prev) => {
      const next = new Map(prev);
      toAssign.forEach((svm) => {
        next.set(svm.sample.id, {
          id: v4(),
          originalId: batch.id,
          batchNumber: batch.batchNumber,
          sampleType: batch.sampleType,
          growthMedium: batch.growthMedium,
          expiryDate: batch.expiryDate,
        });
      });
      return next;
    });

    const assignedIds = new Set(toAssign.map((svm) => svm.sample.id));
    setSelectedTargets((prev) => prev.filter((id) => !assignedIds.has(id)));
  };

  const handleUnassign = (sampleId: string) => {
    setPendingBatch((prev) => {
      const next = new Map(prev);
      next.set(sampleId, undefined);
      return next;
    });
    setSelectedTargets((prev) => prev.filter((id) => id !== sampleId));
  };

  const onSubmit = () => {
    // Iterate all samples from sets (not just the filtered view) so that
    // samples hidden by growthMedium/sampleType filters are not dropped.
    const samples: UpdateSampleRequest[] = (sets?.items ?? []).flatMap((set) =>
      (set.samples ?? []).map((s) => ({
        sampleSetId: set.id,
        sample: pendingBatch.has(s.id)
          ? { ...s, batch: pendingBatch.get(s.id) }
          : s,
      })),
    );

    const payload: UpdateSamplesRequest = { samples };
    updateSample(payload);
  };

  const selectableItems = availableItems.filter((item) => item.matchesFilters);
  const allSelectableSelected =
    selectableItems.length > 0 &&
    selectableItems.every((item) => selectedTargets.includes(item.sample.id));

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" disabled={disabled}>
          <p>Chargen zuweisen</p>
        </Button>
      </DialogTrigger>
      <DialogContent className="flex min-w-full flex-col p-6 md:min-w-full lg:min-w-5xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">
            Chargenzuweisung
          </DialogTitle>
        </DialogHeader>

        <RTKQueryErrorAlert error={error} />

        <div className="flex h-full flex-col space-y-4">
          <div className="flex flex-row space-x-2">
            <div className="space-y-2">
              <p className="text-sm font-medium">Charge</p>
              <Combobox
                value={selectedBatch}
                onValueChange={(value) => {
                  setSelectedBatch(value ?? "");
                  setSelectedTargets([]);
                }}
                onInputValueChange={setBatchInputValue}
                itemToStringLabel={(id: string) =>
                  (batches?.items ?? []).find((b) => b.id === id)
                    ?.batchNumber ?? ""
                }
              >
                <ComboboxInput placeholder="Charge suchen…" showClear />
                <ComboboxContent className="min-w-[300px]">
                  {!showBatches ? (
                    <p className="text-muted-foreground py-4 text-center text-sm">
                      Suche eingeben…
                    </p>
                  ) : (
                    <>
                      {filteredActive.length === 0 &&
                        filteredExpired.length === 0 && (
                          <p className="text-muted-foreground py-2 text-center text-sm">
                            Keine Chargen gefunden.
                          </p>
                        )}
                      <ComboboxList>
                        {filteredActive.length > 0 && (
                          <ComboboxGroup>
                            <ComboboxLabel>Aktive Chargen</ComboboxLabel>
                            {filteredActive.map((batch) => (
                              <ComboboxItem
                                key={batch.id}
                                value={batch.id}
                                className={cn(
                                  getBatchExpiryStatus(batch.expiryDate) ===
                                    "expiring" && "font-medium text-amber-600",
                                )}
                              >
                                <span className="flex flex-col">
                                  <span className="font-medium">
                                    {batch.batchNumber}
                                  </span>
                                  <span className="text-muted-foreground text-xs">
                                    {formatDate(batch.expiryDate)}
                                  </span>
                                </span>
                                <div className="ml-auto flex items-center gap-1">
                                  <Badge variant="outline" className="text-xs">
                                    {growthMedia[batch.growthMedium]}
                                  </Badge>
                                  <Badge variant="outline" className="text-xs">
                                    {sampleTypes[batch.sampleType]}
                                  </Badge>
                                </div>
                              </ComboboxItem>
                            ))}
                          </ComboboxGroup>
                        )}
                        {filteredActive.length > 0 &&
                          filteredExpired.length > 0 && <ComboboxSeparator />}
                        {filteredExpired.length > 0 && (
                          <ComboboxGroup>
                            <ComboboxLabel>Abgelaufene Chargen</ComboboxLabel>
                            {filteredExpired.map((batch) => (
                              <ComboboxItem
                                key={batch.id}
                                value={batch.id}
                                className="text-red-600"
                              >
                                <span className="flex flex-col">
                                  <span className="font-medium">
                                    {batch.batchNumber}
                                  </span>
                                  <span className="text-xs">
                                    {formatDate(batch.expiryDate)}
                                  </span>
                                </span>
                                <div className="ml-auto flex items-center gap-1">
                                  <Badge variant="outline" className="text-xs">
                                    {growthMedia[batch.growthMedium]}
                                  </Badge>
                                  <Badge variant="outline" className="text-xs">
                                    {sampleTypes[batch.sampleType]}
                                  </Badge>
                                </div>
                              </ComboboxItem>
                            ))}
                          </ComboboxGroup>
                        )}
                      </ComboboxList>
                    </>
                  )}
                </ComboboxContent>
              </Combobox>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">Nährmedium</p>
              <Select value={growthMedium} onValueChange={setGrowthMedium}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="All types..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  {Object.values(GrowthMedium).map((value) => (
                    <SelectItem key={value} value={value}>
                      {growthMedia[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">Probentyp</p>
              <Select value={sampleType} onValueChange={setSampleType}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Alle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  {Object.values(SampleType).map((value) => (
                    <SelectItem key={value} value={value}>
                      {sampleTypes[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <p className="invisible text-sm font-medium">Filter</p>
              <Button
                variant="ghost"
                disabled={growthMedium === "all" && sampleType === "all"}
                onClick={() => {
                  setGrowthMedium("all");
                  setSampleType("all");
                }}
              >
                <X className="h-4 w-4" />
                Filter zurücksetzen
              </Button>
            </div>
          </div>

          <div className="flex-1 space-y-4">
            <div className="grid h-full grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Verfügbare Proben</h3>
                  <div className="flex items-center gap-3">
                    {selectableItems.length > 0 && (
                      <button
                        type="button"
                        className="flex cursor-pointer items-center space-x-2"
                        onClick={() => {
                          const ids = selectableItems.map(
                            (item) => item.sample.id,
                          );
                          if (allSelectableSelected) {
                            setSelectedTargets((prev) =>
                              prev.filter((id) => !ids.includes(id)),
                            );
                          } else {
                            setSelectedTargets((prev) => [
                              ...new Set([...prev, ...ids]),
                            ]);
                          }
                        }}
                      >
                        <Checkbox checked={allSelectableSelected} />
                        <span className="text-sm">Alle auswählen</span>
                      </button>
                    )}
                    <Badge variant="secondary">
                      {availableItems.length} nicht zugewiesen
                    </Badge>
                  </div>
                </div>
                <div className="flex-1 rounded-lg border">
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-2 p-4">
                      {availableItems.length === 0 ? (
                        <div className="text-muted-foreground py-12 text-center">
                          <CheckCircle2 className="mx-auto mb-2 h-8 w-8 opacity-50" />
                          <p className="font-medium">
                            Keine passenden Proben vorhanden
                          </p>
                          <p className="text-sm">
                            Passen Sie die Filter entsprechend an
                          </p>
                        </div>
                      ) : (
                        availableItems?.map((item) => (
                          <div
                            key={item.sample.id}
                            className={cn(
                              "hover:bg-muted/50 hover:border-border bg-muted/30 flex items-center space-x-3 rounded-md border border-transparent p-3 transition-all duration-200",
                            )}
                          >
                            <Checkbox
                              id={item.sample.id}
                              disabled={!item.matchesFilters}
                              checked={selectedTargets.includes(item.sample.id)}
                              onCheckedChange={(checked) => {
                                setSelectedTargets((prev) => {
                                  if (checked) {
                                    if (prev.includes(item.sample.id)) {
                                      return prev;
                                    }
                                    return [...prev, item.sample.id];
                                  }

                                  return prev.filter(
                                    (id) => id !== item.sample.id,
                                  );
                                });
                              }}
                            />
                            <label
                              htmlFor={item.sample.id}
                              aria-label={growthMedia[item.sample.growthMedium]}
                              className="flex-1 cursor-pointer"
                            >
                              <div
                                className={cn(
                                  "flex items-center justify-between",
                                  item.matchesFilters
                                    ? ""
                                    : "text-muted-foreground",
                                )}
                              >
                                <div className="flex flex-col">
                                  <div className="flex flex-row space-x-2">
                                    <SampleSetNumber
                                      sampleSetId={item.sampleSetId}
                                    />
                                    <p>
                                      - {growthMedia[item.sample.growthMedium]}
                                    </p>
                                  </div>
                                  {item.matchesFilters ? (
                                    <div />
                                  ) : (
                                    <p className="text-sm">
                                      Nicht zu dieser Charge zuweisbar
                                    </p>
                                  )}
                                </div>
                                <div className="flex gap-1">
                                  <Badge
                                    variant="secondary"
                                    className="text-xs"
                                  >
                                    {growthMedia[item.sample.growthMedium]}
                                  </Badge>
                                  {getSampleTypeBadge(
                                    sets?.items ?? [],
                                    item.sampleSetId,
                                  )}
                                </div>
                              </div>
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </div>

                <div className="flex justify-center pt-2">
                  <Button
                    onClick={() => handleAssignment()}
                    disabled={!selectedBatch || selectedTargets.length === 0}
                    className="h-12 w-full px-8 text-base"
                    size="lg"
                  >
                    ({selectedTargets.length}) Proben zuweisen
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">
                    Aktuelle Zuweisungen
                  </h3>
                  <Badge variant="secondary">
                    {assignedItems.length} total
                  </Badge>
                </div>

                <div className="flex-1 rounded-lg border">
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-2 p-4">
                      {assignedItems.length === 0 ? (
                        <div className="text-muted-foreground py-12 text-center">
                          <ArrowRight className="mx-auto mb-2 h-8 w-8 opacity-50" />
                          <p className="font-medium">
                            Noch keine Zuweisungen vorhanden
                          </p>
                          <p className="text-sm">
                            Proben auswählen und einer Charge zuweisen
                          </p>
                        </div>
                      ) : (
                        assignedItems.map((assignment) => (
                          <div
                            key={assignment.sample.id}
                            className="bg-muted/30 hover:bg-muted/50 hover:border-border flex items-center justify-between rounded-md border border-transparent p-3 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium">
                                {assignment.sample.batch?.batchNumber}
                              </span>
                              <ArrowRight className="text-muted-foreground h-3 w-3" />
                              <span className="text-muted-foreground text-sm">
                                <div className="flex items-center justify-between">
                                  <p className="flex flex-row space-x-2">
                                    <SampleSetNumber
                                      sampleSetId={assignment.sampleSetId}
                                    />
                                    -
                                    {
                                      growthMedia[
                                        assignment.sample.growthMedium
                                      ]
                                    }
                                  </p>
                                  <div className="flex gap-1">
                                    <Badge
                                      variant="secondary"
                                      className="text-xs"
                                    >
                                      {
                                        growthMedia[
                                          assignment.sample.growthMedium
                                        ]
                                      }
                                    </Badge>
                                    {getSampleTypeBadge(
                                      sets?.items ?? [],
                                      assignment.sampleSetId,
                                    )}
                                  </div>
                                </div>
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleUnassign(assignment.sample.id)
                              }
                              className="hover:bg-destructive/10 hover:text-destructive h-8 w-8 p-0"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </div>
                <div className="flex justify-center pt-2">
                  <Button
                    onClick={onSubmit}
                    className="h-12 w-full px-8 text-base"
                    size="lg"
                  >
                    Speichern
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
